export interface LabelBox {
  id: string;
  x: number;
  y: number;
  width: number;
  height: number;
  // Rotation of the actual text box (degrees) plus its raw metrics. Absent on
  // plain horizontal boxes, whose capsule falls back to width/height.
  angle?: number;
  textWidth?: number;
  textHeight?: number;
  candidates?: readonly LabelCandidate[];
}

export interface LabelCandidate {
  x: number;
  y: number;
}

export interface LabelOffset {
  x: number;
  y: number;
}

export interface LabelCircleObstacle {
  x: number;
  y: number;
  radius: number;
}

export interface LabelSegmentObstacle {
  start: LabelCandidate;
  end: LabelCandidate;
}

export interface LabelBounds {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface LabelCollisionOptions {
  gap?: number;
  bounds?: LabelBounds;
  circles?: readonly LabelCircleObstacle[];
  segments?: readonly LabelSegmentObstacle[];
}

export type LabelOffsets = Record<string, LabelOffset>;

const round = (value: number) => Math.round(value * 1000) / 1000;

// A rotated label is really a thin strip of text: model it as a capsule (the
// segment along the text's axis, inflated by half its height). Rect-based
// tests on the axis-aligned bounds phantom-blocked labels: a 36-char label
// rotated 45° covers a ~170×170 bounding square while the actual text strip
// is 230×14, so the resolver kept displacing labels that never truly touched.
interface Capsule {
  start: LabelCandidate;
  end: LabelCandidate;
  radius: number;
  left: number;
  right: number;
  top: number;
  bottom: number;
}

const capsuleOf = (label: LabelBox): Capsule => {
  const width = label.textWidth ?? label.width;
  const height = label.textHeight ?? label.height;
  const radians = ((label.angle ?? 0) * Math.PI) / 180;
  const halfLength = Math.max(0, (width - height) / 2);
  const deltaX = Math.cos(radians) * halfLength;
  const deltaY = Math.sin(radians) * halfLength;
  const start = { x: label.x - deltaX, y: label.y - deltaY };
  const end = { x: label.x + deltaX, y: label.y + deltaY };
  const radius = height / 2;

  return {
    start,
    end,
    radius,
    left: Math.min(start.x, end.x) - radius,
    right: Math.max(start.x, end.x) + radius,
    top: Math.min(start.y, end.y) - radius,
    bottom: Math.max(start.y, end.y) + radius,
  };
};

// Segment obstacles are static per resolve call; their bounds are computed
// once up front instead of inside every capsule test.
interface BoundedSegment extends LabelSegmentObstacle {
  left: number;
  right: number;
  top: number;
  bottom: number;
}

const segmentWithBounds = (segment: LabelSegmentObstacle): BoundedSegment => ({
  ...segment,
  left: Math.min(segment.start.x, segment.end.x),
  right: Math.max(segment.start.x, segment.end.x),
  top: Math.min(segment.start.y, segment.end.y),
  bottom: Math.max(segment.start.y, segment.end.y),
});

const pointToSegmentDistance = (
  point: LabelCandidate,
  start: LabelCandidate,
  end: LabelCandidate
) => {
  const deltaX = end.x - start.x;
  const deltaY = end.y - start.y;
  const lengthSquared = deltaX * deltaX + deltaY * deltaY;
  const t =
    lengthSquared === 0
      ? 0
      : Math.max(
          0,
          Math.min(
            1,
            ((point.x - start.x) * deltaX + (point.y - start.y) * deltaY) /
              lengthSquared
          )
        );

  return Math.hypot(
    point.x - (start.x + deltaX * t),
    point.y - (start.y + deltaY * t)
  );
};

const cross = (
  first: LabelCandidate,
  second: LabelCandidate,
  third: LabelCandidate
) =>
  (second.x - first.x) * (third.y - first.y) -
  (second.y - first.y) * (third.x - first.x);

const segmentsIntersect = (
  first: LabelSegmentObstacle,
  second: LabelSegmentObstacle
) => {
  const firstStart = first.start;
  const firstEnd = first.end;
  const secondStart = second.start;
  const secondEnd = second.end;
  if (
    Math.max(firstStart.x, firstEnd.x) < Math.min(secondStart.x, secondEnd.x) ||
    Math.max(secondStart.x, secondEnd.x) < Math.min(firstStart.x, firstEnd.x) ||
    Math.max(firstStart.y, firstEnd.y) < Math.min(secondStart.y, secondEnd.y) ||
    Math.max(secondStart.y, secondEnd.y) < Math.min(firstStart.y, firstEnd.y)
  ) {
    return false;
  }

  const firstSideStart = cross(firstStart, firstEnd, secondStart);
  const firstSideEnd = cross(firstStart, firstEnd, secondEnd);
  const secondSideStart = cross(secondStart, secondEnd, firstStart);
  const secondSideEnd = cross(secondStart, secondEnd, firstEnd);

  return (
    firstSideStart * firstSideEnd <= 0 && secondSideStart * secondSideEnd <= 0
  );
};

const segmentToSegmentDistance = (
  first: LabelSegmentObstacle,
  second: LabelSegmentObstacle
) =>
  segmentsIntersect(first, second)
    ? 0
    : Math.min(
        pointToSegmentDistance(first.start, second.start, second.end),
        pointToSegmentDistance(first.end, second.start, second.end),
        pointToSegmentDistance(second.start, first.start, first.end),
        pointToSegmentDistance(second.end, first.start, first.end)
      );

const overlaps = (first: Capsule, second: Capsule, gap: number) =>
  first.left - gap < second.right &&
  second.left - gap < first.right &&
  first.top - gap < second.bottom &&
  second.top - gap < first.bottom &&
  segmentToSegmentDistance(first, second) < first.radius + second.radius + gap;

const intersectsCircle = (
  capsule: Capsule,
  circle: LabelCircleObstacle,
  gap: number
) => {
  const reach = circle.radius + gap;

  return (
    circle.x > capsule.left - reach &&
    circle.x < capsule.right + reach &&
    circle.y > capsule.top - reach &&
    circle.y < capsule.bottom + reach &&
    pointToSegmentDistance(circle, capsule.start, capsule.end) <
      capsule.radius + gap + circle.radius
  );
};

const intersectsSegment = (
  capsule: Capsule,
  segment: BoundedSegment,
  gap: number
) =>
  capsule.left - gap < segment.right &&
  segment.left - gap < capsule.right &&
  capsule.top - gap < segment.bottom &&
  segment.top - gap < capsule.bottom &&
  segmentToSegmentDistance(capsule, segment) < capsule.radius + gap;

const overflowAmount = (label: LabelBox, bounds: LabelBounds | undefined) => {
  if (!bounds) {
    return 0;
  }

  const left = label.x - label.width / 2;
  const right = label.x + label.width / 2;
  const top = label.y - label.height / 2;
  const bottom = label.y + label.height / 2;

  return (
    Math.max(0, bounds.x - left) +
    Math.max(0, right - (bounds.x + bounds.width)) +
    Math.max(0, bounds.y - top) +
    Math.max(0, bottom - (bounds.y + bounds.height))
  );
};

const fallbackCandidates = (label: LabelBox, gap: number) => {
  const step = label.height + gap;

  return [
    { x: label.x, y: label.y },
    ...Array.from({ length: 12 }, (_, index) => {
      const distance = Math.ceil((index + 1) / 2) * step;

      return {
        x: label.x,
        y: label.y + (index % 2 === 0 ? distance : -distance),
      };
    }),
  ];
};

interface CandidateScoreOptions {
  candidate: LabelBox;
  source: LabelBox;
  placedCapsules: readonly Capsule[];
  circles: readonly LabelCircleObstacle[];
  segments: readonly BoundedSegment[];
  bounds?: LabelBounds;
  gap: number;
}

const candidateScore = ({
  candidate,
  source,
  placedCapsules,
  circles,
  segments,
  bounds,
  gap,
}: CandidateScoreOptions) => {
  const capsule = capsuleOf(candidate);
  const labelCollisions = placedCapsules.filter(placed =>
    overlaps(capsule, placed, gap)
  ).length;
  const circleCollisions = circles.filter(circle =>
    intersectsCircle(capsule, circle, gap)
  ).length;
  const segmentCollisions = segments.filter(segment =>
    intersectsSegment(capsule, segment, gap)
  ).length;
  const overflow = overflowAmount(candidate, bounds);
  const blocked =
    labelCollisions +
    circleCollisions +
    segmentCollisions +
    Number(overflow > 0);
  const movement = Math.hypot(candidate.x - source.x, candidate.y - source.y);

  return blocked * 1_000_000_000 + overflow * 1_000_000 + movement;
};

/**
 * Chooses the least-obstructed candidate for each label in stable graph order.
 * Greedy O(n²) worst case, but every pairwise test is bbox-prefiltered and
 * capsules/segment bounds are computed once, so spread-out graphs (labels
 * rarely share a neighborhood) stay near-linear well into the hundreds.
 * a spatial grid is the upgrade path if a profile ever shows the
 * broad phase itself dominating.
 */
export const resolveLabelCollisions = (
  labels: readonly LabelBox[],
  options: number | LabelCollisionOptions = 4
): LabelOffsets => {
  const {
    gap = 4,
    bounds,
    circles = [],
    segments = [],
  } = typeof options === 'number' ? { gap: options } : options;
  const boundedSegments = segments.map(segmentWithBounds);
  const ordered = [...labels].sort(
    (a, b) => a.y - b.y || a.x - b.x || (a.id < b.id ? -1 : 1)
  );
  const placedCapsules: Capsule[] = [];
  const offsets: LabelOffsets = {};

  for (const label of ordered) {
    const candidates =
      label.candidates && label.candidates.length > 0
        ? label.candidates
        : fallbackCandidates(label, gap);
    let selected = { ...label, ...candidates[0] };
    let selectedScore = candidateScore({
      candidate: selected,
      source: label,
      placedCapsules,
      circles,
      segments: boundedSegments,
      bounds,
      gap,
    });

    for (const candidate of candidates.slice(1)) {
      const positioned = { ...label, ...candidate };
      const score = candidateScore({
        candidate: positioned,
        source: label,
        placedCapsules,
        circles,
        segments: boundedSegments,
        bounds,
        gap,
      });

      if (score < selectedScore) {
        selected = positioned;
        selectedScore = score;
      }
    }

    const offset = {
      x: round(selected.x - label.x),
      y: round(selected.y - label.y),
    };
    if (offset.x !== 0 || offset.y !== 0) {
      offsets[label.id] = offset;
    }
    placedCapsules.push(capsuleOf(selected));
  }

  return offsets;
};
